import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from 'shared/context/AuthContext';
import { NotificacionesProvider, useNotificaciones } from 'shared/context/NotificacionesContext';
import { DarkModeProvider } from 'shared/context/DarkModeContext';
import useNotificacionesSocket from 'shared/hooks/useNotificacionesSocket';
import { setupPushNotifications } from 'shared/pushNotifications';
import AlertaCritica from 'shared/components/AlertaCritica';
import socket from 'shared/socket';
import { FiAlertTriangle, FiLogOut } from 'react-icons/fi';
import Login from './pages/Login';
import Layout from './components/Layout/Layout';
import { TrabajadorPanel } from './components/Trabajador';
import Configuracion from './pages/Configuracion';
import Inventario from './pages/Inventario';
import Produccion from './pages/Produccion';
import Compras from './pages/Compras';
import Tesoreria from './pages/Tesoreria';
import Ventas from './pages/Ventas';
import Usuarios from './pages/Usuarios';
import Sedes from './pages/Sedes';
import Pago from './pages/Pago';
import BotIA from './pages/BotIA';
import PedidosTienda from './pages/PedidosTienda';

import CierreCaja from './pages/CierreCaja';
import CierreCajaAdmin from './pages/CierreCajaAdmin';
import Clientes from './pages/Clientes';
import Vendedores from './pages/Vendedores';
import VendedorPanel from './pages/VendedorPanel';
import DashboardAdmin from './pages/DashboardAdmin';
import Reportes from './pages/Reportes';
import Asistencia from './pages/Asistencia';
import SuperAdminDashboard from './pages/SuperAdmin/Dashboard';
import SuperAdminEmpresas from './pages/SuperAdmin/EmpresasList';
import SuperAdminUsuarios from './pages/SuperAdmin/Usuarios';
import SuperAdminPagos from './pages/SuperAdmin/Pagos';
import SuperAdminSolicitudes from './pages/SuperAdmin/SolicitudesCambio';
import SuperAdminPlanes from './pages/SuperAdmin/Planes';
import SuperAdminMetrics from './pages/SuperAdmin/Metrics';
import SuperAdminAvisos from './pages/SuperAdmin/Avisos';
import EmpresaConfig from './pages/SuperAdmin/EmpresaConfig';
import API from 'shared/api';
import { usePersistentState } from 'shared/hooks';
import { flushQueue, getQueueCount } from 'shared/attendanceQueue';
import { DeviceBridgeProvider } from 'shared/deviceBridge';
import BridgeInbox from 'shared/components/BridgeInbox';

const PAGE_KEY = 'agorax_worker_page';

function AdminApp() {
  const {
    usuario,
    cargando,
    isAdmin,
    isSuperAdmin,
    isSupervisor,
    tienePermiso,
    isPlanExterno,
    isTipoEmpresa,
    logout,
    refrescarPermisos,
  } = useAuth();
  const { notificar } = useNotificaciones();
  const [currentPage, setCurrentPage] = usePersistentState(PAGE_KEY, 'chats');
  const [hadPersistedPage] = useState(() => {
    try {
      return localStorage.getItem(PAGE_KEY) !== null;
    } catch {
      return false;
    }
  });
  const paginaInicialSet = useRef(hadPersistedPage);

  // Notificaciones centrales por area (filtradas por permiso + sonido fuerte).
  useNotificacionesSocket();

  useEffect(() => {
    if (!cargando && usuario && !paginaInicialSet.current) {
      paginaInicialSet.current = true;
      if (isSuperAdmin()) return;
      const tipoEmpresa = usuario?.tipo_empresa || 'general';
      const paginas = [
        {
          id: 'chats',
          permiso: ['chat.acceder', 'pedidos.armado', 'pedidos.cocinero', 'pedidos.delivery'],
        },
        { id: 'vendedores-panel', permiso: 'vendedores.panel' },
        { id: 'dashboard', permiso: 'dashboard.ver' },
        { id: 'reportes', permiso: 'dashboard.ver' },
        { id: 'inventario', permiso: 'inventario.ver' },
        { id: 'compras', permiso: 'compras.ver' },
        { id: 'tesoreria', permiso: 'tesoreria.ver' },
        {
          id: 'produccion',
          permiso: 'produccion.ver',
          tipoExcluir: ['importador', 'general'],
        },
        {
          id: 'pedidos-tienda',
          permiso: 'pedidos_tienda.pedido_ver',
          plan: 'interno',
          tipoExcluir: ['importador', 'general'],
        },
        { id: 'clientes', permiso: 'clientes.ver' },
        { id: 'usuarios', permiso: 'usuarios.ver' },
        { id: 'sedes', permiso: 'sedes.ver' },
        { id: 'pago', permiso: 'plan.ver' },
        { id: 'bot-ia', permiso: 'bot.ver' },
        { id: 'asistencia', permiso: 'asistencia.acceder' },
        { id: 'configuracion', permiso: 'config.perfil' },
      ];
      for (const p of paginas) {
        if (p.plan === 'interno' && isPlanExterno()) continue;
        if (p.tipoExcluir && p.tipoExcluir.includes(tipoEmpresa)) continue;
        if (tienePermiso(p.permiso)) {
          setCurrentPage(p.id);
          return;
        }
      }
    }
  }, [cargando, usuario, isSuperAdmin, isPlanExterno, tienePermiso, setCurrentPage]);

  // Re-evaluar ruta inicial cuando se refrescan permisos (login en otro dispositivo, cambios remotos)
  const permisosJson = JSON.stringify(usuario?.permisos || []);
  const prevPermisosRef = useRef(permisosJson);
  useEffect(() => {
    if (
      !cargando &&
      usuario &&
      prevPermisosRef.current !== permisosJson &&
      paginaInicialSet.current
    ) {
      paginaInicialSet.current = false;
    }
    prevPermisosRef.current = permisosJson;
  }, [permisosJson, cargando, usuario]);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [empresaConfig, setEmpresaConfig] = useState(null);
  const [fuerzaLogout, setFuerzaLogout] = useState(false);

  useEffect(() => {
    const handler = (data) => {
      if (!data) return;
      if (!tienePermiso('clientes.ver')) return;
      const nombre = [data.nombre, data.apellido].filter(Boolean).join(' ') || 'Un cliente';
      notificar({
        titulo: 'Nueva solicitud de registro',
        mensaje: `${nombre} solicitó acceso a la aplicación.`,
        tipo: 'fase',
        page: 'clientes',
        severidad: 'normal',
      });
    };
    socket.on('nueva_solicitud_registro', handler);
    return () => {
      socket.off('nueva_solicitud_registro', handler);
    };
  }, [notificar, tienePermiso]);

  useEffect(() => {
    const unirseAsesores = () => {
      const user = JSON.parse(localStorage.getItem('usuario') || 'null');
      const idempresa = user?.idempresa;
      if (idempresa) {
        socket.emit('join_room', `e${idempresa}_asesores`);
      }
    };
    if (socket.connected) unirseAsesores();
    socket.on('connect', unirseAsesores);
    return () => {
      socket.off('connect', unirseAsesores);
    };
  }, []);

  useEffect(() => {
    if (usuario && !isSuperAdmin()) {
      API.get('/empresa-config/')
        .then((r) => {
          if (r.data.status === 'success') setEmpresaConfig(r.data.data);
        })
        .catch((e) => console.error('Error cargando empresa config:', e?.message || e));
    }
  }, [usuario, isSuperAdmin]);

  // Notificaciones push web (Web Push / VAPID): pedir permiso y suscribir el
  // dispositivo una vez que hay un usuario autenticado.
  useEffect(() => {
    if (usuario) {
      setupPushNotifications();
    }
  }, [usuario]);

  // Manifest dinamico del admin con el branding de la empresa (logo + colores).
  useEffect(() => {
    const slug = empresaConfig?.slug || localStorage.getItem('agorax_empresa_slug');
    if (!slug) return;
    const api = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    link.setAttribute(
      'href',
      `${api}/publico/manifest-admin?slug=${encodeURIComponent(slug)}`
    );
  }, [empresaConfig]);

  const meFlag = useRef(false);
  useEffect(() => {
    if (!usuario || meFlag.current) return;
    meFlag.current = true;
    refrescarPermisos().catch((e) => {
      if (e.response?.status === 401) {
        setFuerzaLogout(
          'Tu perfil ha sido eliminado o desactivado. Contacta al administrador si crees que es un error.'
        );
      }
    });
  }, [usuario]); // eslint-disable-line

  useEffect(() => {
    const handler = (data) => {
      setFuerzaLogout(data?.message || 'Tu perfil ha sido eliminado por el administrador.');
    };
    socket.on('perfil_eliminado', handler);
    return () => {
      socket.off('perfil_eliminado', handler);
    };
  }, []);

  useEffect(() => {
    const handler = (data) => {
      if (!data || Object.keys(data).length === 0) {
        refrescarPermisos();
        return;
      }
      const u = JSON.parse(localStorage.getItem('usuario') || 'null');
      if (!u) return;
      if (data.permisos !== undefined) u.permisos = data.permisos;
      if (data.role_id !== undefined) u.role_id = data.role_id;
      if (data.rol_nombre !== undefined) u.rol_nombre = data.rol_nombre;
      localStorage.setItem('usuario', JSON.stringify(u));
      refrescarPermisos();
    };
    socket.on('permisos_actualizados', handler);
    return () => {
      socket.off('permisos_actualizados', handler);
    };
  }, [refrescarPermisos]);

  useEffect(() => {
    if (!usuario) return;
    const n = getQueueCount();
    if (n > 0) console.log('[App] Pendientes de asistencia:', n);
    flushQueue(API).then(() => {
      const after = getQueueCount();
      if (after > 0) console.log('[App] Quedan', after, 'pendientes tras flush');
    });
    const interval = setInterval(() => flushQueue(API), 5000);
    return () => clearInterval(interval);
  }, [usuario]);

  const handleFuerzaLogout = () => {
    setFuerzaLogout(false);
    logout();
  };

  if (cargando) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'sans-serif',
        }}
      >
        Cargando...
      </div>
    );
  }

  if (fuerzaLogout) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        }}
      >
        <div
          style={{
            background: 'var(--agx-surface)',
            borderRadius: '20px',
            padding: '32px 36px',
            maxWidth: '420px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}
        >
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <FiAlertTriangle size={28} color="#dc2626" />
          </div>
          <h2
            style={{
              fontSize: '20px',
              fontWeight: 700,
              color: 'var(--agx-text)',
              margin: '0 0 8px',
              letterSpacing: '-0.2px',
            }}
          >
            Perfil Eliminado
          </h2>
          <p
            style={{
              fontSize: '14px',
              color: 'var(--agx-muted)',
              margin: '0 0 24px',
              lineHeight: '1.6',
            }}
          >
            {typeof fuerzaLogout === 'string'
              ? fuerzaLogout
              : 'Tu perfil ha sido eliminado por el administrador. Debes salir de la aplicacion.'}
          </p>
          <button
            onClick={handleFuerzaLogout}
            style={{
              padding: '12px 32px',
              borderRadius: '12px',
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background 0.15s',
            }}
          >
            <FiLogOut size={16} /> Salir
          </button>
        </div>
      </div>
    );
  }

  if (!usuario) {
    return (
      <Login
        onLogin={(userData) => {
          localStorage.setItem('usuario', JSON.stringify(userData));
          if (userData.empresa_slug) {
            localStorage.setItem('agorax_empresa_slug', userData.empresa_slug);
          }
          window.location.reload();
        }}
        empresaSlug={null}
      />
    );
  }

  if (isSuperAdmin()) {
    if (selectedEmpresa) {
      return <EmpresaConfig empresaId={selectedEmpresa} onBack={() => setSelectedEmpresa(null)} />;
    }
    const renderSuperPage = () => {
      switch (currentPage) {
        case 'dashboard':
          return <SuperAdminDashboard />;
        case 'empresas':
          return <SuperAdminEmpresas onSelectEmpresa={setSelectedEmpresa} />;
        case 'usuarios':
          return <SuperAdminUsuarios />;
        case 'pagos':
          return <SuperAdminPagos />;
        case 'planes':
          return <SuperAdminPlanes />;
        case 'solicitudes':
          return <SuperAdminSolicitudes />;
        case 'metrics':
          return <SuperAdminMetrics />;
        case 'avisos':
          return <SuperAdminAvisos />;
        default:
          return <SuperAdminDashboard />;
      }
    };
    return (
      <Layout currentPage={currentPage} onPageChange={setCurrentPage} isSuperAdmin={true}>
        {renderSuperPage()}
      </Layout>
    );
  }

  const renderPage = () => {
    const SinAcceso = () => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.4 }}>
            <FiAlertTriangle size={48} />
          </div>
          <h2
            style={{ fontSize: 20, fontWeight: 700, color: 'var(--agx-text)', margin: '0 0 8px' }}
          >
            Sin acceso
          </h2>
          <p
            style={{ fontSize: 14, color: 'var(--agx-muted)', margin: '0 0 24px', lineHeight: 1.5 }}
          >
            Tu rol actual no tiene permisos para acceder a esta sección. Contacta al administrador
            de tu empresa para que te asigne los permisos necesarios.
          </p>
        </div>
      </div>
    );
    const tienePanel =
      tienePermiso('chat.acceder') ||
      tienePermiso('pedidos.delivery') ||
      tienePermiso('pedidos.armado') ||
      tienePermiso('pedidos.cocinero');
    const fallback = tienePanel ? <TrabajadorPanel /> : <SinAcceso />;
    switch (currentPage) {
      case 'chats':
        return tienePanel ? <TrabajadorPanel /> : <SinAcceso />;
      case 'inventario':
        return isAdmin() || tienePermiso('inventario.ver') ? (
          <Inventario onNavigate={setCurrentPage} />
        ) : (
          fallback
        );
      case 'compras':
        return isAdmin() || tienePermiso('compras.ver') ? (
          <Compras onNavigate={setCurrentPage} />
        ) : (
          fallback
        );
      case 'tesoreria':
        return isAdmin() || tienePermiso('tesoreria.ver') ? (
          <Tesoreria />
        ) : (
          fallback
        );
      case 'ventas':
        return isAdmin() || tienePermiso('ventas.ver') ? (
          <Ventas />
        ) : (
          fallback
        );
      case 'produccion':
        return (isAdmin() || tienePermiso('produccion.ver')) &&
          isTipoEmpresa('restaurante') ? (
          <Produccion onNavigate={setCurrentPage} />
        ) : (
          fallback
        );
      case 'usuarios':
        return isAdmin() || isSupervisor() || tienePermiso('usuarios.ver') ? (
          <Usuarios />
        ) : (
          fallback
        );
      case 'sedes':
        return isAdmin() || isSupervisor() || tienePermiso('sedes.ver') ? (
          <Sedes
            readOnly={!isAdmin() && !tienePermiso('sedes.gestionar')}
            onNavigate={setCurrentPage}
          />
        ) : (
          fallback
        );
      case 'pago':
        return isAdmin() || tienePermiso('plan.ver') ? <Pago /> : fallback;
      case 'bot-ia':
        return isAdmin() || tienePermiso('bot.ver') ? <BotIA /> : fallback;
      case 'pedidos-tienda':
        return (isAdmin() ||
          tienePermiso('pedidos_tienda.salon_ver') ||
          tienePermiso('pedidos_tienda.pedido_ver') ||
          tienePermiso('pedidos_tienda.espera_ver') ||
          tienePermiso('pedidos_tienda.historial_ver')) &&
          isTipoEmpresa('restaurante') ? (
          <PedidosTienda />
        ) : (
          fallback
        );

      case 'cierre-caja':
        return (isAdmin() || tienePermiso('cierre_caja.ver')) &&
          isTipoEmpresa('restaurante') ? (
          <CierreCaja />
        ) : (
          fallback
        );
      case 'cierre-caja-admin':
        return (isAdmin() || tienePermiso('cierre_caja.admin')) &&
          isTipoEmpresa('restaurante') ? (
          <CierreCajaAdmin />
        ) : (
          fallback
        );
      case 'clientes':
        return isAdmin() || tienePermiso('clientes.ver') ? <Clientes /> : fallback;
      case 'vendedores':
        return (isAdmin() || isSupervisor() || tienePermiso('vendedores.ver')) &&
          isTipoEmpresa('importador') ? (
          <Vendedores />
        ) : (
          fallback
        );
      case 'vendedores-panel':
        return tienePermiso('vendedores.panel') && isTipoEmpresa('importador') ? <VendedorPanel /> : fallback;
      case 'dashboard':
        return tienePermiso('dashboard.ver') ? (
          <DashboardAdmin onNavigate={setCurrentPage} />
        ) : (
          fallback
        );
      case 'reportes':
        return tienePermiso('dashboard.ver') ||
          tienePermiso([
            'reportes.ventas',
            'reportes.inventario',
            'reportes.produccion',
            'reportes.cierres',
            'reportes.clientes',
            'reportes.usuarios',
            'reportes.ia',
          ]) ? (
          <Reportes />
        ) : (
          fallback
        );
      case 'configuracion':
        return tienePermiso('config.perfil') ? (
          <Configuracion onNavigate={setCurrentPage} />
        ) : (
          fallback
        );
      case 'asistencia':
        return tienePermiso('asistencia.acceder') ? <Asistencia /> : fallback;
      default:
        return fallback;
    }
  };

  return (
    <Layout
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      empresaConfig={empresaConfig}
    >
      {renderPage()}
      <AlertaCritica onVer={setCurrentPage} />
      <BridgeInbox />
    </Layout>
  );
}

function App() {
  return (
    <DarkModeProvider>
      <AuthProvider>
        <BrowserRouter>
          <NotificacionesProvider>
            <DeviceBridgeProvider>
              <Routes>
                <Route path="/*" element={<AdminApp />} />
              </Routes>
            </DeviceBridgeProvider>
          </NotificacionesProvider>
        </BrowserRouter>
      </AuthProvider>
    </DarkModeProvider>
  );
}

export default App;
